export const CURSOR_CONTENT = {
  layout: 'process',
  title: 'Redesigning Cursor’s onboarding for developers at every level',
  timeline: 'March – May 2025',
  roles: 'UX Research Lead, UI Designer',
  team: 'Shrushti Chavan, Tiffany Chau',
  tools: 'Figma, Dovetail, UserTesting',
  impactNotes: [
    'Led 6 research sessions and turned first-run failures into a ranked fix list.',
    'Designed the onboarding redesign and iterated it against test feedback.',
    'Kept scope tight: every change traces back to an observed struggle.',
  ],
  editorialNav: [
    { id: 'impact', label: 'Impact' },
    { id: 'overview', label: 'Overview' },
    { id: 'problem', label: 'Problem' },
    { id: 'research', label: 'Research' },
    { id: 'testing', label: 'Testing' },
    { id: 'solution', label: 'Solution' },
    { id: 'reflection', label: 'Reflection' },
  ],
  process: [
    {
      id: 'overview',
      bucket: 'overview',
      title: 'Overview',
      heading: 'Does Cursor really work for beginners and experts?',
      body: ['A concept study testing Cursor’s claim that it works for every skill level, focused on the first session.'],
    },
    {
      id: 'problem',
      bucket: 'problem',
      title: 'Problem',
      heading: 'New users stall before they reach the AI.',
      bullets: [
        'Onboarding is hard to follow',
        'Hidden features, unfamiliar terms',
        'Starting a project takes too long',
      ],
      subsections: [
        {
          title: 'Our goals',
          items: [
            'Onboarding anyone can follow',
            'Fewer errors and hesitations',
            'A fast, direct way to start a project',
          ],
        },
      ],
      images: [
        { src: '/images/cursor-problem-1.png', caption: 'No clear way to start a new project.' },
        { src: '/images/cursor-problem-2.png', caption: 'Shortcuts lack context, and only three are shown.' },
      ],
    },
    {
      id: 'audit',
      bucket: 'research',
      title: 'Evaluative research',
      heading: 'Competitive audit',
      body: ['How do other AI coding tools onboard new users?'],
      challenges: [
        { number: '01', title: 'Cursor · AI code editor', text: 'Powerful, but tuned for VS Code habits.' },
        { number: '02', title: 'Warp · AI terminal', text: 'Wide reach, but mistakes are costly.' },
        { number: '03', title: 'Codeium · AI assistant', text: 'Quick for small builds.' },
      ],
    },
    {
      id: 'v1',
      bucket: 'research',
      title: 'How might we',
      heading: 'How might we make Cursor’s first session clear and fast for everyone?',
      body: ['Version 1: clearer entry points, simpler navigation, a guided first run.'],
      imageLayout: 'compare',
      images: [
        { src: '/images/cursor-v1-before.png', label: 'Before' },
        { src: '/images/cursor-v1-after.gif', label: 'After · Version 1' },
      ],
    },
    {
      id: 'unmoderated',
      bucket: 'testing',
      title: 'Unmoderated testing',
      heading: 'Download, set up, first use',
      body: ['3 unmoderated sessions with experienced developers on UserTesting.'],
      findings: [
        {
          title: 'No way to start fresh',
          text: 'Only “Open Project” or “Clone Repo” were offered.',
          quote: { title: 'Participant', text: 'Cursor by default does not have any option for me to create a project.' },
        },
        { title: 'Confusing jargon', text: 'Terms like “connect via SSH” intimidated beginners.' },
        { title: 'Silent install', text: 'Nothing confirmed the download had worked.' },
        { title: 'What worked', text: 'AI suggestions were easy to trigger with Ctrl+I or Tab.' },
      ],
    },
    {
      id: 'moderated',
      bucket: 'testing',
      title: 'Moderated testing',
      heading: 'Prototype walkthroughs',
      body: ['3 moderated Zoom sessions: two Cursor users, one newcomer.'],
      quotes: [
        { title: 'Participant 1', quote: 'Before I signed up, I wanted something telling me what Cursor was.' },
        { title: 'Participant 2', quote: 'These three buttons will benefit beginners who just want to start live coding.' },
      ],
      findings: [
        { title: 'Explain Cursor first', text: 'People wanted to know what it is before signing up.' },
        { title: 'Show starting paths', text: 'Guided entry points got beginners coding faster.' },
        { title: 'Clearer layers', text: 'Popovers like search need stronger visual cues.' },
        { title: 'Accessibility', text: 'Text size and contrast held older users back.' },
      ],
      images: [{ src: '/images/cursor-eval.png' }],
    },
    {
      id: 'solution',
      bucket: 'solution',
      title: 'Solution',
      heading: 'A guided first run for every level',
      body: ['Explain Cursor before signup, make starting a project one step, and give beginners clear entry points.'],
      images: [{ src: '/images/cursor-solution.gif' }],
    },
    {
      id: 'reflection',
      bucket: 'reflection',
      title: 'Looking back',
      heading: 'What I learned from Cursor.',
      learnings: [
        { title: 'Small changes, big relief', text: 'A clearer button did more for first-run confidence than any new feature.' },
        { title: 'Research ends debates', text: 'Observed behavior beat opinions in every review.' },
        { title: 'Design for a range of skill', text: 'Beginners and experts share a product, not a mental model.' },
      ],
      nextSteps: [
        { title: 'Test with true beginners', text: 'Every participant so far had coding experience. The next round should include people writing their first project.' },
        { title: 'Build in accessibility from day one', text: 'Text size and contrast came up late. Bring them into the onboarding system from the start rather than fixing them after.' },
      ],
    },
  ],
};

export const COALIGN_CONTENT = {
  title: 'Coalign: A Tangible Collaboration System for Product Teams',
  timeline: 'October - November 2025',
  roles: 'Product Designer, Interaction Designer, Fabrication',
  team: 'Shrushti Chavan, Suri Liu, Alfreda Liu',
  tools: 'Figma, Laser Cutter',
  skills: 'Interaction Design, Prototyping, Fabrication',
  problem:
    'Product teams run on Agile, but collaboration still breaks down. Every week brings meetings, milestones, and deadlines — yet the hardest part isn\'t the work itself. It\'s making sure feedback moves through the team. Critiques get scattered across Slack, Figma comments, and meeting notes. Decisions get buried in threads. Design variations are impossible to track.',
  goals: [
    'Bridge the gap between creative ideation and structured critique across roles',
    'Make feedback tangible, transparent, and actionable in real time',
    'Support designers, developers, and managers in a single shared workspace',
  ],
  goalsGridCols: 3,
  hmw:
    'How might we design a tangible interface that enables product teams to collaboratively explore, compare, and critique designs — so that feedback becomes structured, transparent, and actionable?',
  summary:
    'A shared workspace where teams present, critique, and iterate together, hands-on, in real time.',
  marketLandscape:
    'We designed around three archetypes — each with different responsibilities, goals, and pain points — but all sharing one workspace.',
  competitors: [
    { name: 'Designers', logo: '/images/coalign-archetype-designer.png', desc: 'Present designs, collect feedback, and iterate' },
    { name: 'Engineers', logo: '/images/coalign-archetype-engineer.png', desc: 'Evaluate feasibility and translate designs to code' },
    { name: 'Managers', logo: '/images/coalign-archetype-manager.png', desc: 'Track decisions and keep the project on track' },
  ],
  competitorsNoCard: true,
  prototypeImage: '/images/coalign-prototype.png',
  v1Demo: '/images/coalign-v1.gif',
  v1Summary: 'Our first prototype centered on a pen-based controller that let designers present, annotate, and A/B test designs on a shared screen. We mapped the full user journey to understand where each role enters the flow.',
  speakingToUsers:
    'After our first prototype, we realized the system was too designer-centric. Non-designers lacked clear ways to participate, and the single pen controller created turn-taking friction that slowed collaboration.',
  unmoderatedIntro: '',
  unmoderatedQuotes: [],
  moderatedIntro:
    'Each prototype cycle revealed new friction points that shaped the next iteration.',
  moderatedFindings: [
    { title: 'Limited Role Support', text: 'Developers and managers had no meaningful way to interact — only designers had agency.' },
    { title: 'Complex Onboarding', text: 'First-time users couldn\'t figure out the workflow without step-by-step guidance.' },
    { title: 'Single Controller Bottleneck', text: 'One shared pen meant awkward turn-taking — multiple users needed to engage at once.' },
  ],
  solutionImage: '/images/coalignfinal.gif',
  learnings: [
    {
      title: 'Physical interaction changes the problem',
      text: 'Gesture, material, and space are the interface. Tangible systems force decisions about form that screens can hide behind layout.',
    },
    {
      title: 'Shared space makes critique concrete',
      text: 'When people can point and manipulate together, feedback stops floating in threads and attaches to the artifact in front of them.',
    },
    {
      title: 'Novel models need guided first use',
      text: 'Without familiar UI patterns, setup cues and microinteractions decide whether the system is usable or abandoned.',
    },
  ],
};

export const SPIRA_CONTENT = {
  layout: 'process',
  title: 'Redesigning Spira’s Home so new users know where to start',
  timeline: 'September 2026 – present',
  roles: 'Product Design Intern',
  tools: 'Figma, Claude Code',
  process: [
    {
      id: 'overview',
      title: 'Overview',
      body: ['Spira has dozens of AI tools for creators. I redesigned the first screen people see, so a new user knows what to do in the first few seconds.'],
    },
    {
      id: 'problem',
      title: 'Problem',
      heading: 'New users had every tool, but didn’t know where to start.',
      body: ['Through an audit of the product, conversations with PMs and engineers, and watching new-user sessions in PostHog, I found three core problems:'],
      bullets: [
        'New users weren’t sure what to do first.',
        'They couldn’t see everything Spira can make.',
        'Nothing on the first screen spoke to their own brand.',
      ],
      annotated: {
        label: 'Before',
        src: '/images/spira/home-before.jpg',
        alt: 'Spira’s old Tools page, led by a Spira Maxima banner',
        pins: [
          { x: 11, y: 21.7, areas: [[1, 18.9, 15.5, 5.8]], text: 'Social Studio is first in the nav, but it’s empty until you add your brand.' },
          { x: 52, y: 8, areas: [[19.5, 3.5, 78.4, 39.1]], text: 'Almost half the screen is a banner for one tool.' },
          { x: 39.5, y: 33.6, areas: [[21.2, 30.8, 15.8, 5.6]], text: 'The only action is “Try Spira Maxima.” There’s nowhere to type what you want.' },
          { x: 30.5, y: 82, areas: [[19.5, 50.5, 25.5, 37], [10.5, 37.5, 6, 3.3], [19.5, 3.5, 78.4, 39.1]], text: 'Spira Maxima shows up three times: badge, banner, and first card.' },
          { x: 54, y: 94.4, areas: [[19.5, 91.8, 78.4, 5.3]], text: 'The full tool list starts below the fold.' },
        ],
      },
    },
    {
      id: 'shift',
      title: 'The shift',
      heading: 'Land everyone where they can start.',
      pivotContrast: {
        before: 'Promote the newest tool.',
        after: 'Give every user one clear place to start, with every tool in view.',
      },
    },
    {
      id: 'home',
      title: 'The new Home',
      heading: 'One box to start, every tool in view.',
      annotated: {
        label: 'After',
        src: '/images/spira/home-after.jpg',
        alt: 'The redesigned Spira Home with a Video prompt box, Start here examples, and all tools',
        pins: [
          { x: 14.8, y: 8.3, areas: [[1, 6, 17, 4.6]], text: 'Your brand sits at the top, so everything is made for it.' },
          { x: 6, y: 23.6, areas: [[0.8, 22, 16.8, 35.5]], text: 'The sidebar is split into Create and Grow.' },
          { x: 53.5, y: 24.5, areas: [[29.8, 16.1, 58.3, 19.7]], text: 'One box to start, with Agent, Video, and Image modes.' },
          { x: 29.5, y: 41.9, areas: [[21.5, 40.3, 75, 26.3]], text: 'Start here shows what you can make before you type.' },
          { x: 28.5, y: 70.5, areas: [[21.5, 69, 75, 14.7]], text: 'Every tool is on the first screen.' },
        ],
      },
    },
    {
      id: 'superagent',
      title: 'SuperAgent',
      heading: 'Describe it, and the agent does the rest.',
      body: ['For people who don’t know the tool names yet. Skills and files are one click from the box.'],
      images: [{ src: '/images/spira/superagent.jpg' }],
    },
    {
      id: 'reflection',
      title: 'Looking back',
      heading: 'What I learned from Spira.',
      learnings: [
        { title: 'Design the first five seconds', text: 'A new user decides fast. The first screen has to answer “what do I do here?”' },
        { title: 'Cut the repeats', text: 'Showing one tool three times crowded out everything else.' },
        { title: 'Explore wide, then commit', text: 'I tried many directions before landing on input first.' },
      ],
      nextSteps: [
        { title: 'Test with new users', text: 'Watch first sessions to see if people make something sooner.' },
      ],
    },
  ],
};

export const OMNIGENCE_CONTENT = {
  layout: 'process',
  title: 'Redesigning Omnigence.ai’s brand and product direction',
  timeline: 'February-April 2026',
  roles: 'Product Design Intern',
  team: 'Kevin Fong, Timson Tan, Berny Sesay',
  focus: 'Product Design · Web · AI',
  liveUrl: 'https://omnigence-ai.vercel.app/',
  impactNotes: [
    'Designed 3+ core workflows for a multi-agent AI platform.',
    'Built 20+ reusable components for a consistent product.',
    'Shipped the landing page and 3+ features to production with Cursor and Claude Code.',
    'Saved ~45 engineering hours by building approved designs in code.',
  ],
  process: [
    {
      id: 'overview',
      bucket: 'overview',
      title: 'Overview',
      heading: 'Making a complex AI product easy to understand',
      body: ['Powerful tech, unclear story. Over three months I redesigned and shipped the landing and dashboard UX with the engineering team.'],
    },
    {
      id: 'problem',
      bucket: 'problem',
      title: 'Problem',
      challenges: [
        { number: '1', title: 'Value unclear in seconds', text: 'Visitors couldn’t tell what it did or why it mattered.' },
        { number: '2', title: 'AI concepts outran the UI', text: 'Too technical for buyers, too vague for experts.' },
        { number: '3', title: 'Trust was invisible', text: 'Data safety lived in policy, not the product.' },
      ],
      images: [{ src: '/images/omnigence-hero-before.png' }],
    },
    {
      id: 'exploring',
      bucket: 'exploring',
      title: 'Exploring',
      heading: 'Clear and credible on first pass, without losing depth.',
    },
    {
      id: 'exploring-comprehension',
      bucket: 'exploring',
      title: '',
      heading: 'Lead with the problem, not the feature list',
      issueItems: [
        'Didn’t say what the product does',
        'Didn’t name the problem it solves',
        'Structure buried the story',
      ],
      solutionItems: [
        'Plain-language story up front',
        'Hierarchy that matches how people decide',
        'A step-by-step How It Works',
      ],
    },
    {
      id: 'exploring-trust',
      bucket: 'exploring',
      title: '',
      heading: 'Put security in the interface',
      body: ['Promises like “Zero data retention” live on the page, not in the footer.'],
      images: [{ src: '/images/omnigence/landing-redesign.png' }],
    },
    {
      id: 'exploring-system',
      bucket: 'exploring',
      title: '',
      heading: 'Build just enough system',
      body: ['No design system existed, so I defined the color, type, and components that the landing page and dashboard share.'],
      images: [
        { src: '/images/omnigence-research-1.png' },
        { src: '/images/omnigence-research-2.png' },
        { src: '/images/omnigence-research-3.png' },
        { src: '/images/omnigence-research-4.png' },
      ],
      carousel: true,
    },
    {
      id: 'solutions',
      bucket: 'solutions',
      title: 'Solutions',
      heading: 'What went live',
      body: ['The landing page and core product web experience shipped to production.'],
      images: [{ src: '/images/omnigence/approve-mockup.jpg' }],
    },
    {
      id: 'roadmap',
      bucket: 'roadmap',
      title: 'Looking back',
      heading: 'What I learned from Omnigence.',
      learnings: [
        { title: 'Speaking engineering’s language', text: 'Shipping in code saved the team ~45 hours.' },
        { title: 'Owning it on a small team', text: 'No handoffs. I took the work from sketch to merged code.' },
        { title: 'Designing under constraint', text: 'No design system, so I built just enough to move fast.' },
      ],
      nextSteps: [
        { title: 'Test the new story with real buyers', text: 'Check that it builds trust, not just looks better.' },
      ],
    },
  ],
};

export const GUIDELY_CONTENT = {
  layout: 'process',
  showImageLabels: false,
  processMediaFirst: true,
  title: 'Guidely turns student confusion into something students and teachers can act on.',
  timeline: 'September – December 2025',
  roles: 'Product Designer (group project)',
  team: 'Olivia Stenerson, Egie Kwon, Jingyuan Gao',
  tools: 'Figma, Adobe Photoshop',
  focus: 'Product Design · AI UX · Research',
  impactNotes: [
    'Reframed the brief from “prevent AI dependence” to “make confusion actionable.”',
    'Connected student annotations and teacher insight into one loop.',
    'Shaped a direction schools could realistically adopt.',
  ],
  process: [
    {
      id: 'overview',
      title: 'Overview',
      heading: 'An AI learning tool that starts where students get stuck.',
      body: ['Students highlight what confuses them and get guidance in context. Teachers see where the class is stuck.'],
    },
    {
      id: 'starting-point',
      title: 'The Starting Point',
      heading: 'We started with AI dependence.',
      mediaFirst: false,
      body: ['AI makes answers easy, but answers aren’t understanding.'],
      callout: {
        label: 'Our first question',
        text: 'How might we help students use AI more responsibly?',
      },
      calloutAfterMedia: true,
      images: [
        { src: '/images/guidely/guidely-core-problem-flow.png', canvasVariant: 'figma-dots' },
      ],
    },
    {
      id: 'research',
      title: 'Research',
      heading: 'Research reframed the problem.',
      mediaFirst: false,
      body: ['15 interviews and prototype tests with students and teachers.'],
      findings: [
        { title: 'Students need help mid-confusion', text: 'They engaged more when AI kept them thinking.' },
        { title: 'Teachers need visibility', text: 'They couldn’t see confusion until grades showed it.' },
      ],
      images: [{ src: '/images/guidely/guidely-pew-teachers.png' }],
    },
    {
      id: 'pivot',
      title: 'The Pivot',
      heading: 'The real problem: what happens when students get stuck.',
      pivotContrast: {
        before: 'How might we stop students from over-relying on AI?',
        after: 'How might we help students work through confusion, and show teachers where?',
      },
      closingLine: 'That shift became Guidely.',
    },
    {
      id: 'idea',
      title: 'The Idea',
      heading: 'What if confusion became the interface?',
      body: ['Most AI tools start with an empty box. Guidely starts with what students mark in their own coursework.'],
    },
    {
      id: 'student-experience',
      title: 'Student Experience',
      heading: 'Start with the part that doesn’t make sense.',
      flowSteps: ['Highlight', 'Name the confusion', 'Explore guided questions', 'Keep learning'],
      images: [{ src: '/images/guidely/guidely-annotation.mp4' }],
    },
    {
      id: 'teacher-experience',
      title: 'Teacher Experience',
      heading: 'Confusion becomes a pattern teachers can see.',
      body: ['Annotations roll up into a class heatmap, so gaps show up before the quiz does.'],
      quotes: [
        { title: 'Aryelle (Teacher)', quote: 'This actually helps me plan tomorrow’s lesson instead of guessing.' },
      ],
      images: [{ src: '/images/guidely/guidely-teacher-dashboard.mp4' }],
    },
    {
      id: 'loop',
      title: 'How It Connects',
      heading: 'One question can reveal what the whole class needs.',
      mediaFirst: false,
      body: ['Students create the signals; teachers act on them. That loop is the product.'],
      flowTracks: [
        { title: 'Student flow', steps: ['Course material', 'Annotate confusion', 'Get guided help', 'Keep learning'] },
        { title: 'Teacher flow', steps: ['Annotations', 'Class patterns', 'Heatmap', 'Instruction adapts'] },
      ],
      subsections: [
        { title: 'When AI isn’t enough', text: 'Context carries over to human support, so students never re-explain.' },
      ],
      images: [
        { src: '/images/guidely/guidely-counselor-support.mp4', canvasColor: '#3e3aff' },
      ],
    },
    {
      id: 'reflection',
      title: 'Looking back',
      heading: 'What I learned from Guidely.',
      learnings: [
        { title: 'Change the question', text: 'Reframing around confusion, not AI misuse, gave us a real product direction.' },
        { title: 'Let research overrule the brief', text: '15 interviews moved us from restricting AI to designing for the moment students get stuck.' },
        { title: 'Design the loop, not the screen', text: 'The student and teacher experiences only worked once each fed the other.' },
      ],
      nextSteps: [
        { title: 'Test the heatmap with teachers', text: 'Check whether teachers can read it and act on it quickly, mid-lesson.' },
        { title: 'Define the hand-off to a person', text: 'Decide when AI guidance should pass a student to a teacher or counselor, with context intact.' },
        { title: 'Fit into existing classroom tools', text: 'Explore how Guidely lives inside the LMS tools schools already use.' },
      ],
    },
  ],
};

export const BABBS_CONTENT = {
  layout: 'process',
  title: 'Brand refresh for a classic coffeehouse',
  subtitle:
    'Independent brand project — modernizing Babb’s visual identity while protecting the warmth the community already trusts.',
  roles: 'Brand Designer (independent)',
  timeline: 'Summer 2026',
  focus: 'Branding · Identity · Visual System',
  nav: [
    { id: 'overview', label: 'Overview' },
    { id: 'problem', label: 'Problem' },
    { id: 'direction', label: 'Direction' },
    { id: 'identity', label: 'Identity' },
    { id: 'applications', label: 'Applications' },
    { id: 'reflection', label: 'Tradeoffs' },
  ],
  process: [
    {
      id: 'overview',
      number: '01',
      title: 'Overview',
      heading: 'A hometown cafe whose product outpaced its identity',
      body: [
        'Babb’s is a local coffee shop with years of community goodwill. The coffee and space held up; the brand system did not — it read dated and undercut the quality of the experience inside.',
        'This was an independent brand refresh: modernize the visual identity without erasing the character that made Babb’s feel like Babb’s. Scope is identity and applications — not a product redesign.',
      ],
      images: [],
    },
    {
      id: 'problem',
      number: '02',
      title: 'Problem',
      heading: 'Quality in the cup, ambiguity in the mark',
      body: [
        'The existing identity failed to signal product quality or place character. It was hard to recognize at distance or in social feeds — a recognition problem, not a coffee problem.',
      ],
      images: [
        { src: '/images/babbs-website-before-1.png', caption: '' },
        { src: '/images/babbs-website-before-2.png', caption: '' },
      ],
    },
    {
      id: 'direction',
      number: '03',
      title: 'Direction',
      heading: 'Contemporary without alienating regulars',
      body: [
        'The constraint was dual: feel current for new customers, stay familiar for people who already love the shop. The system had to hold across signage, packaging, menus, and digital — and still read as Babb’s.',
      ],
      images: [],
    },
    {
      id: 'identity',
      number: '04',
      title: 'Identity',
      heading: 'From ornamental mark to cleaner wordmark',
      body: [
        'Kept warmth; removed dated ornament. The new wordmark prioritizes recognition and flexibility across print and digital.',
      ],
      imageLayout: 'compare',
      images: [
        { src: '/images/babbs-logo-original.png', label: 'Original', caption: '' },
        { src: '/images/babbs-logo-new.png', label: 'New', caption: '' },
      ],
    },
    {
      id: 'applications',
      number: '05',
      title: 'Applications',
      heading: 'System in use',
      body: [],
      images: [],
    },
    {
      id: 'reflection',
      number: '06',
      title: 'Tradeoffs',
      heading: 'Emotion vs. implementability',
      body: [
        'Small-business brand work is negotiation between attachment to the old mark and the need to modernize. Every choice had to be buildable — signage, print, digital — not just conceptually interesting. I protected familiarity where it carried equity, and cut ornament where it blocked recognition.',
      ],
      images: [],
    },
  ],
};

export const WORK_ITEMS = [
  {
    name: 'Spira.AI',
    pill: 'B2C AI tool',
    thumb: { ratio: '1.15', fit: 'cover', position: '45% 55%' },
    slug: 'spira',
    eyebrow: 'Internship 2026',
    cardCaption: 'AI tools for creators',
    concept: 'INTERNSHIP · FALL 2026',
    year: '2026',
    coverType: 'spira-bot',
    img: '/images/spira-welcome.mp4',
    fullContent: SPIRA_CONTENT,
  },
  {
    name: 'Omnigence.ai',
    pill: 'B2B AI platform',
    thumb: { ratio: '0.78', fit: 'contain', position: 'center bottom' },
    eyebrow: 'Internship 2026',
    cardCaption: 'Rebranding an early-stage AI product',
    concept: 'INTERNSHIP · SHIPPED · SPRING 2026',
    desc: 'Owned landing and dashboard UX for a vertical AI product — shipped live.',
    href: 'https://www.figma.com/slides/Vv4YwkKOzdWy50MD0Y9Emb',
    img: '/images/omnigence-cover.mp4',
    year: '2026',
    tags: ['Product Design', 'AI', 'Shipped'],
    shipped: true,
    fullContent: OMNIGENCE_CONTENT,
  },
  {
    name: 'Guidely',
    pill: 'Educational design',
    thumb: { ratio: '0.95', fit: 'contain' },
    slug: 'guidely',
    eyebrow: 'Concept 2026',
    cardCaption: 'Rethinking AI in the classroom',
    concept: 'CONCEPT · 2025',
    desc: 'A concept for making student confusion visible — through annotation, contextual AI guidance, and a teacher heatmap.',
    img: '/images/guidely-cover.mp4',
    heroImg: '/images/guidely/guidely-hero.mp4',
    year: '2025',
    cardBg: 'linear-gradient(150deg, #ececee 0%, #e4e4e7 100%)',
    tags: ['Product Design', 'AI UX', 'Concept'],
    fullContent: GUIDELY_CONTENT,
  },
  {
    name: 'Cursor',
    pill: 'Concept design',
    eyebrow: 'Concept 2026',
    cardCaption: 'Coding tools for every skill level',
    concept: 'CONCEPT · SPRING 2025',
    desc: 'Concept study: first-run onboarding for mixed-skill developers.',
    href: 'https://www.figma.com/deck/VwG12N2t5D0DXCFJYhfGr7/Cursor-Case-Study?node-id=1-316&viewport=-11116%2C-55%2C0.43&t=oKnft1IIlCJOariS-1&scaling=min-zoom&content-scaling=fixed&page-id=0%3A1',
    img: '/images/cursor-cover.mp4',
    year: '2025',
    cardBg: 'linear-gradient(150deg, #1c1c1e 0%, #2c2c30 100%)',
    tags: ['Concept', 'UX Research', 'UI'],
    fullContent: CURSOR_CONTENT,
  },
  /* Coalign — hidden for now
  {
    name: 'Coalign',
    slug: 'coalign',
    concept: 'CONCEPT · FALL 2025',
    cardCaption: 'Tangible collaboration system for cross-functional teams',
    desc: 'A shared workspace for presenting, critiquing, and iterating together in real time.',
    img: '/images/coalign-cover.gif',
    cardBg: '#94bdf1',
    fullContent: COALIGN_CONTENT,
  },
  */
  /* Belonging — hidden for now
  {
    name: 'Belonging: Stories for Kids Finding Their Place',
    concept: 'COMING SOON',
    year: '2026',
    desc: 'An interactive storytelling app for kids exploring cultural identity',
    comingSoon: true,
    comingSoonPath: '/coming-soon/belonging',
    coverType: 'belonging-characters',
    cardBg: '#fffdf9',
    tags: ['Product Design', 'Storytelling'],
  },
  */
  /* Babb's — hidden for now; restore by uncommenting. BABBS_CONTENT kept below.
  {
    name: "Babb's",
    slug: 'babbs',
    concept: 'BRAND · INDEPENDENT',
    year: '2026',
    desc: 'Independent brand refresh for a local coffeehouse — identity, not product UX.',
    img: '/images/babbs-cover.png',
    cardBg: 'linear-gradient(150deg, #f5ebe0 0%, #d5c4a1 100%)',
    tags: ['Brand', 'Identity'],
    fullContent: BABBS_CONTENT,
  },
  */
];
